import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import type {
  ToolActionHandlers,
  ToolActionKind,
} from '@/components/agent/tools/_shared/chat-tool-actions'
import { ToolCardActions } from '@/components/agent/tools/_shared/ToolCardActions'
import { RunCypherView } from '@/components/agent/tools/RunCypherView'
import { api } from '@/services/api'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import { getHydratedNodeLabel } from '@/utils/entity-display'
import {
  type BackendPayload,
  extractEntityIds,
  parseResult,
  type RunCypherArgs,
} from './run-cypher'
import { useApplyContributionToGraph } from './useApplyContributionToGraph'
import { useToolCardActions } from './useToolCardActions'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface RunCypherRenderProps {
  args: RunCypherArgs
  result?: string | BackendPayload
  status: Status
}

/**
 * Connected `run_cypher` tool card: parses the backend payload, hydrates
 * referenced nodes into a GraphView for the canvas actions, resolves the
 * action footer from the chat context, and hands pure data to RunCypherView.
 */
function RunCypherRender({ args, result, status }: RunCypherRenderProps) {
  const { caseId } = useParams<{ caseId: string }>()
  const applyToGraph = useApplyContributionToGraph(caseId)
  const [pending, setPending] = useState<ToolActionKind | null>(null)

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = parsed?.kind === 'error' || status.type === 'incomplete'
  const entityIds = useMemo(() => extractEntityIds(parsed), [parsed])

  // Build the action handlers. The cypher result carries only ids, so we
  // hydrate full nodes (labels, properties, name) via the graph-hydrate API
  // before handing a GraphView to the canvas. Without hydration the canvas
  // renders chips labelled "Default" because the GraphView snapshot itself
  // is what's drawn — the canvas does not re-fetch by id.
  const handlers = useMemo<ToolActionHandlers>(() => {
    if (!caseId || entityIds.length === 0) return {}
    const label = `Cypher · ${entityIds.length} node${entityIds.length === 1 ? '' : 's'}`

    const hydrate = async (): Promise<GraphView | null> => {
      try {
        const data = await api.graph.hydrateGraph(caseId, {
          node_ids: entityIds,
          relationship_ids: [],
        })
        return {
          meta: {
            source: 'assistant',
            createdAt: new Date().toISOString(),
            caseId,
            label,
          },
          // `getHydratedNodeLabel` walks the documented fallback chain
          // (`_display_name` → API name → type-specific keys → generic
          // name-ish keys → short id), so chips show a meaningful label
          // even when the backend left `n.name` empty.
          nodes: data.nodes.map(n => ({
            id: n.id,
            labels: n.labels,
            typeLabel: n.labels?.[0],
            name: getHydratedNodeLabel(n),
            properties: n.properties ?? {},
          })),
          edges: data.relationships.map(r => ({
            id: r.id,
            type: r.type,
            source: r.source,
            target: r.target,
            properties: r.properties ?? {},
          })),
          view: emptyViewState(),
        }
      } catch (e) {
        console.error('[RunCypher] hydrate failed:', e)
        toast.error('Failed to load result on graph')
        return null
      }
    }

    // Hydration is a network round-trip; flag the clicked action so its button
    // shows a spinner (and the footer locks) until we navigate away or fail.
    const run = async (kind: ToolActionKind, mode: 'explore' | 'add') => {
      setPending(kind)
      try {
        const view = await hydrate()
        if (!view) return
        applyToGraph(mode, view, label)
      } finally {
        setPending(null)
      }
    }
    return {
      showOnGraph: () => void run('showOnGraph', 'explore'),
      showSeparately: () => void run('showSeparately', 'explore'),
      addToGraph: () => void run('addToGraph', 'add'),
    }
  }, [caseId, entityIds, applyToGraph])

  const actionsModel = useToolCardActions(entityIds, handlers)
  const actions = actionsModel ? (
    <ToolCardActions model={actionsModel} handlers={handlers} pending={pending} />
  ) : null

  return (
    <RunCypherView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      actions={actions}
    />
  )
}

export const RunCypherToolUI = makeAssistantToolUI<RunCypherArgs, string | BackendPayload>({
  toolName: 'run_cypher',
  render: RunCypherRender,
})
